// ExpandedTalentList.js
import React, { useEffect } from "react";
import { Box, Button, Snackbar, Alert } from "@mui/material";
import {
  Favorite,
  FavoriteBorder,
  Notes,
  AttachFile as AttachFileIcon,
  Description as DescriptionIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import TalentCard from "../../../talent/components/TalentCard";
import { saveTalentNote } from "../Submission/TalentNoteService";

const ExpandedTalentList = ({
  currentListName,
  talentDetails,
  isLoading,
  favorites,
  notes,
  editingNote,
  setEditingNote,
  noteText,
  setNoteText,
  sortBy,
  setSortBy,
  setFavorites,
  setNotes,
  setMoveStatus,
  moveStatus,
  setTalentDetails,
  userId,
  navigate,
  setIsReplyDrawerOpen,
  setSelectedMessage,
  handleMessageClick,
  handleMoveTalent,
  handleFavouriteClick,
  toggleSelection,
  selectedTalents,
  submissions,
}) => {
  // --- Handle sort dropdown change ---
  const handleSortChange = (e) => {
    const key = e.target.value;
    setSortBy(key);
  };

  // --- Automatically re-sort when sortBy changes ---
  useEffect(() => {
    if (!talentDetails?.length) return;

    setTalentDetails((prev) =>
      [...prev].sort((a, b) => {
        if (sortBy === "MostRecent")
          return new Date(b.recordCreatedDate) - new Date(a.recordCreatedDate);
        if (sortBy === "MostLikes")
          return (b.mostLiked || 0) - (a.mostLiked || 0);
        if (sortBy === "Experience")
          return (b.experience || 0) - (a.experience || 0);
        return 0;
      }),
    );
  }, [sortBy]);

  // --- Notes handling ---
 const openNoteEditor = (talent) => {
  setEditingNote(talent.userId);
  setNoteText(talent.note || "");
};
  const saveNote = (talent) => {
    saveTalentNote({
      talentId: talent.userId,
      noteText,
      directorId: userId,
      noteId: talent.noteId || 0,
    }).then(() => {
      setTalentDetails(prev =>
  prev.map(t =>
    t.userId === talent.userId
      ? {
          ...t,
          note: noteText,
          noteId: t.noteId
        }
      : t
  )
);
      setEditingNote(null);
    });
  };

  // --- Get current list code (A/B/C/X) ---
  const getCurrentList = (userId, roleId) => {
    const match = submissions.find((s) => s.roleId === roleId);
    if (!match) return null;
    if (match.aListUserIds?.includes(userId.toString())) return "A List";
    if (match.bListUserIds?.includes(userId.toString())) return "B List";
    if (match.cListUserIds?.includes(userId.toString())) return "C List";
    if (match.xListUserIds?.includes(userId.toString())) return "X List";
    if (match.newUserIds?.includes(userId.toString())) return "New";
    return null;
  };

  const mapListLabelToCode = {
    "A List": "A",
    "B List": "B",
    "C List": "C",
    "X List": "X",
  };

  return (
    <div>
      {/* --- Move success toast --- */}
      {moveStatus && (
        <Snackbar
          open={moveStatus}
          autoHideDuration={3000}
          onClose={() => setMoveStatus(false)}
        >
          <Alert severity="success" onClose={() => setMoveStatus(false)}>
            Talent Moved successfully
          </Alert>
        </Snackbar>
      )}

      {/* --- Sort dropdown --- */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          margin: "10px 0",
        }}
      >
        <div>
          <label htmlFor="sortBy">Sort By:</label>
          <select
            id="sortBy"
            name="sortBy"
            value={sortBy}
            className="customSelectSort"
            onChange={handleSortChange}
          >
            <option value="MostRecent">Newest</option>
            <option value="MostLikes">Most Likes</option>
            <option value="Experience">Experience</option>
          </select>
        </div>

        {/* --- Legend --- */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "10px 15px",
            borderRadius: "10px",
            fontSize: "14px",
            color: "#000",
            maxWidth: "1000px",
          }}
        >
          <span>A - A List</span>
          <span>B - B List</span>
          <span>C - C List</span>
          <span>D - D List</span>
          <span>X - X List</span>
          <LinkIcon />
          <span>Additional Link</span>
          <AttachFileIcon /> <span>Attachment</span>
          <DescriptionIcon /> <span>Notes</span>
          <Favorite style={{ color: "rgb(209, 124, 124)", cursor: "pointer" }} />
          <FavoriteBorder style={{ color: "black", cursor: "pointer" }} />
          <span>Add/Remove From Favorites</span>
          <Notes style={{ color: "black", cursor: "pointer" }} />
          <span>Add Notes</span>
        </div>
      </div>

      {/* --- Talent Cards --- */}
      {isLoading ? (
        <div
          style={{
            minHeight: "600px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="spinner" />
          <p>Loading talent details...</p>
        </div>
      ) : talentDetails.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            px: 1,
          }}
        >
          {talentDetails.map((talent) => {
            const dynamicListName = getCurrentList(
              talent.userId,
              talent.roleId,
            );
            return (
              <Box
                key={talent.userId}
                sx={{
                  flex: "1 1 250px",
                  maxWidth: "25%",
                  minWidth: "250px",
                  boxSizing: "border-box",
                }}
              >
                {editingNote === talent.userId ? (
                  <Box>
                    <h3>Add a note</h3>
                    <textarea
                      style={{ width: "100%", minHeight: 80 }}
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Add a note about this person for future reference"
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 1,
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => saveNote(talent)}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setEditingNote(null)}
                      >
                        Discard
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <TalentCard
                    profile={{
  ...talent,
  favorite: favorites[talent.userId] ?? talent.favorite,
}}
                    brokenImageIds={[]}
                    handleFavouriteClick={(e, id, fav) =>
                      handleFavouriteClick(e, id, fav)
                    }
                    handleMessageClick={handleMessageClick}
                    navigate={navigate}
                    showListDropdown={true}
                    currentListName={mapListLabelToCode[dynamicListName]}
                    onListChange={(userId, targetList) =>
                      handleMoveTalent(
                        userId,
                        talent.roleId,
                        dynamicListName,
                        targetList,
                      )
                    }
                    onNoteClick={() => openNoteEditor(talent)}
                    onSelect={() => toggleSelection(talent)}
                    isSubmissionView={true}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      ) : (
        <p>No talent details found</p>
      )}

      {/* --- Action bar for multi-select --- */}
      {selectedTalents?.length > 0 && (
        <div className="action-bar fixed bottom-0 left-0 w-full bg-gray-800 text-white p-4 flex justify-center gap-4">
          <button className="action-button bg-blue-500 px-4 py-2 rounded">
            Action 1
          </button>
          <button className="action-button bg-green-500 px-4 py-2 rounded">
            Action 2
          </button>
        </div>
      )}
    </div>
  );
};

export default ExpandedTalentList;
